import { GlobalPrimaryBannerPicture } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/GlobalPrimaryBannerPicture'
import {
  GlobalPrimaryBannerWithList,
  GlobalPrimaryBannerWithTextOverlay,
  GravityPicture,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  BannerProps,
  BoxHorizontalAlignment,
  TextHorizontalAlignment,
} from '@thg-commerce/enterprise-widget-banner'
import { ButtonEmphases } from '@thg-commerce/gravity-elements'

import { WidgetTypes } from '../WidgetTypes'

interface PrimaryBannerWithList
  extends Omit<GlobalPrimaryBannerWithList, 'id' | 'query'> {
  useH1?: string
  hasMargin?: string
}

interface CtaProps {
  text: string
  link: string
  emphasis: ButtonEmphases
}

const getButtonEmphasis = (contentTheme, ctaOverride) => {
  if (contentTheme === 'dark' || ctaOverride === 'dark') {
    return 'high'
  }
  return 'medium'
}
const setStyling = (tpStyling, fallback) => tpStyling || fallback

const createCta = (
  text: string | undefined,
  link: string | undefined,
  contentTheme: string | undefined,
  ctaOverride: string | undefined,
) => {
  if (text && link) {
    return {
      text,
      link,
      emphasis: getButtonEmphasis(contentTheme, ctaOverride) as ButtonEmphases,
    }
  }
  return null
}

export const getPrimaryBannerProps = (
  banner:
    | GlobalPrimaryBannerPicture
    | (Omit<GlobalPrimaryBannerWithTextOverlay, 'query'> & {
        picture: GravityPicture
      })
    | (Omit<PrimaryBannerWithList, 'query'> & { picture: GravityPicture }),
): BannerProps => {
  const ctas = Object.entries(banner)
    .filter(([key]) => key.startsWith('cta') && !key.endsWith('URL'))
    .map(([key, text]) => ({
      index: ['One', 'Two', 'Three', 'Four', 'Five', 'Six'].indexOf(
        key.replace('cta', ''),
      ),
      cta: createCta(
        text as string,
        (banner as any)[`${key}URL`],
        banner.contentTheme,
        banner.ctaOverride,
      ),
    }))
    .filter(({ cta }) => cta)
    .sort((a, b) => a.index - b.index)
    .map(({ cta }) => cta)

  return {
    picture: banner.picture,
    imageLink: banner.bannerURL || undefined,
    has6Buttons: banner.display6Buttons,
    headingTextColor: banner.headingTitleColor,
    subtitleTextColor: banner.subtitleTextColor,
    backgroundColor: banner.backgroundColor,
    primaryBannerStyle: banner.primaryBannerStyle,
    enableBannerTextOverlay:
      banner.__typename === WidgetTypes.GlobalPrimaryBannerWithTextOverlay,

    logoImage: {
      default: banner.logopngImageBG,
      white: banner.logopngWhiteBG,
      altText: banner?.altImageLarge || undefined,
    },
    title:
      (banner.headline && {
        text: banner.headline,
        headingLevel: banner.useH1 && banner.useH1 === 'true' ? 'h1' : 'h2',
      }) ||
      undefined,
    ctas: ctas.length ? (ctas as [CtaProps, ...CtaProps[]]) : undefined,
    subtitle: banner.subtitle || '',
    hasMargin: setStyling(banner.hasMargin, undefined),
    textColor: banner.contentTheme === 'light' ? 'light' : 'dark',
    textContainerOptions: {
      width: 'full',
      contentAlignment: {
        box: (banner.contentBoxPosition as BoxHorizontalAlignment) || 'middle',
        text: (banner.contentAlign as TextHorizontalAlignment) || 'center',
      },
    },
  }
}
